import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { readCookieConsent } from '../../lib/cookieConsent';
import {
  trackFirstVisitCustom,
  trackGlobalButtonClick,
  trackGlobalLinkClick,
  trackPageViewEnriched,
  trackScrollDepth,
  trackSectionView,
  trackSessionStartCustom,
  trackTimeOnPage,
} from '../../utils/analytics';

function getSectionFromElement(el: HTMLElement): string {
  const sectionCarrier = el.closest<HTMLElement>('[data-analytics-section]');
  return sectionCarrier?.dataset.analyticsSection || 'global';
}

function getReadableLabel(el: HTMLElement): string {
  return (
    el.getAttribute('aria-label') ||
    el.getAttribute('title') ||
    el.getAttribute('data-analytics-label') ||
    el.textContent?.replace(/\s+/g, ' ').trim() ||
    'unknown'
  ).slice(0, 100);
}

function getStableButtonId(el: HTMLElement): string {
  return (
    el.getAttribute('id') ||
    el.getAttribute('name') ||
    el.getAttribute('data-analytics-id') ||
    el.getAttribute('aria-label') ||
    (el.className || '').toString().split(/\s+/).filter(Boolean).slice(0, 2).join('_') ||
    'unknown_button'
  )
    .replace(/\s+/g, '_')
    .slice(0, 80);
}

const SCROLL_MARKERS: Array<25 | 50 | 75 | 100> = [25, 50, 75, 100];

const GlobalAnalyticsObserver: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!readCookieConsent()?.analytics) return;
    const isNewSession = !window.sessionStorage.getItem('reff_analytics_session_seen_v1');
    if (isNewSession) {
      window.sessionStorage.setItem('reff_analytics_session_seen_v1', '1');
      trackSessionStartCustom();
    }
    const isFirstVisit = !window.localStorage.getItem('reff_analytics_first_visit_seen_v1');
    if (isFirstVisit) {
      window.localStorage.setItem('reff_analytics_first_visit_seen_v1', '1');
      trackFirstVisitCustom();
    }
    trackPageViewEnriched(pathname);
  }, [pathname]);

  useEffect(() => {
    if (!readCookieConsent()?.analytics) return;
    const t30 = window.setTimeout(() => trackTimeOnPage({ page_path: pathname, seconds: 30 }), 30000);
    const t60 = window.setTimeout(() => trackTimeOnPage({ page_path: pathname, seconds: 60 }), 60000);
    return () => {
      window.clearTimeout(t30);
      window.clearTimeout(t60);
    };
  }, [pathname]);

  useEffect(() => {
    if (!readCookieConsent()?.analytics) return;

    const seen = new Set<number>();
    const onScroll = () => {
      const doc = document.documentElement;
      const maxScrollable = doc.scrollHeight - window.innerHeight;
      if (maxScrollable <= 0) return;
      const pct = Math.round((window.scrollY / maxScrollable) * 100);
      for (const marker of SCROLL_MARKERS) {
        if (pct >= marker && !seen.has(marker)) {
          seen.add(marker);
          trackScrollDepth({ page_path: pathname, percent: marker });
        }
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [pathname]);

  useEffect(() => {
    if (!readCookieConsent()?.analytics) return;

    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const clickable = target.closest<HTMLElement>('button, a');
      if (!clickable) return;

      const label = getReadableLabel(clickable);
      const section = getSectionFromElement(clickable);

      if (clickable.tagName.toLowerCase() === 'button') {
        trackGlobalButtonClick({
          label,
          button_id: getStableButtonId(clickable),
          page_path: pathname,
          section,
          element_tag: 'button',
        });
        return;
      }

      const href = (clickable as HTMLAnchorElement).getAttribute('href') || '';
      trackGlobalLinkClick({
        label,
        page_path: pathname,
        section,
        destination: href,
      });
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [pathname]);

  useEffect(() => {
    if (!readCookieConsent()?.analytics) return;

    const elements = Array.from(document.querySelectorAll<HTMLElement>('[data-analytics-section]'));
    if (!elements.length) return;
    const seenSections = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const section = (entry.target as HTMLElement).dataset.analyticsSection || '';
          if (!section || seenSections.has(section)) continue;
          seenSections.add(section);
          trackSectionView({ page_path: pathname, section });
        }
      },
      { threshold: 0.35 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [pathname]);

  return null;
};

export default GlobalAnalyticsObserver;
